// Workstream B — cross-loan Obligations / Cost-Recovery ledger page at
// /portfolio-admin/cost-recovery. READ-ONLY: a consolidated view of every
// obligation the Payment System v3 money engine produces, filterable by
// settlement channel and obligation state via URL params. Loans.Read is
// enforced server-side; this page is reachable to anyone with the nav entry.

import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Card,
  EmptyState,
  PageHeader,
  SkeletonList,
  StatusBadge,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import type {
  ObligationLedgerRowDto,
  ObligationState,
  SettlementChannel,
} from "@/api/portfolioAdmin/obligationLedgerApi";
import { useCostRecoveryLedger } from "@/hooks/portfolioAdmin/useObligationLedger";
import {
  channelLabel,
  stateLabel,
  stateTone,
} from "@/components/portfolioAdmin/loanDetail/financials/LoanObligationsPanel";

const CHANNEL_OPTIONS: ReadonlyArray<{ value: "" | SettlementChannel; label: string }> = [
  { value: "", label: "All channels" },
  { value: "phoenix", label: "Phoenix" },
  { value: "servicer", label: "Servicer" },
];

const STATE_OPTIONS: ReadonlyArray<{ value: "" | ObligationState; label: string }> = [
  { value: "", label: "All states" },
  { value: "pending", label: "Pending" },
  { value: "partially_paid", label: "Partially paid" },
  { value: "paid_in_full", label: "Paid in full" },
  { value: "waived", label: "Waived" },
  { value: "charged_off", label: "Charged off" },
  { value: "superseded", label: "Superseded" },
];

const SELECT_CLASS =
  "rounded-md border border-border-light bg-white-pure px-space-3 py-space-2 text-small text-onyx";

export default function CostRecoveryPage() {
  const [params, setParams] = useSearchParams();
  // Defaults derived at parse time — no post-mount effect that re-keys the query.
  const settlementChannel = params.get("settlementChannel") ?? "";
  const state = params.get("state") ?? "";

  const queryParams = useMemo(
    () => ({
      settlementChannel: settlementChannel || undefined,
      state: state || undefined,
    }),
    [settlementChannel, state],
  );
  const query = useCostRecoveryLedger(queryParams);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const data = query.data;

  return (
    <div className="flex flex-col gap-space-4" data-testid="cost-recovery-page">
      <PageHeader
        eyebrow="Portfolio administration"
        title="Cost recovery"
        description="Obligations the payment engine owes across the portfolio — original, recovered to date, and remaining."
      />

      <Card>
        <div className="flex flex-wrap items-end justify-between gap-space-3 mb-space-3">
          <div className="flex flex-wrap items-center gap-space-3">
            <label className="flex flex-col gap-space-1 text-caption uppercase tracking-label text-gray-mid">
              Settlement channel
              <select
                className={SELECT_CLASS}
                value={settlementChannel}
                onChange={(e) => setParam("settlementChannel", e.target.value)}
                data-testid="cost-recovery-channel-filter"
              >
                {CHANNEL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-space-1 text-caption uppercase tracking-label text-gray-mid">
              State
              <select
                className={SELECT_CLASS}
                value={state}
                onChange={(e) => setParam("state", e.target.value)}
                data-testid="cost-recovery-state-filter"
              >
                {STATE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {data && (
            <div className="flex items-center gap-space-4 text-small text-gray-mid">
              <SummaryStat label="Original" value={data.summary.totalOriginal} />
              <SummaryStat label="Recovered" value={data.summary.totalRecovered} />
              <SummaryStat label="Remaining" value={data.summary.totalRemaining} />
            </div>
          )}
        </div>

        {query.isLoading ? (
          <SkeletonList rows={6} data-testid="cost-recovery-loading" />
        ) : query.isError || !data ? (
          <p className="text-small text-status-error m-0" data-testid="cost-recovery-error">
            Cost recovery could not be loaded.
          </p>
        ) : data.rows.length === 0 ? (
          <EmptyState
            variant="noResults"
            title="No obligations match"
            description="No cost-recovery obligations match the current filters."
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Loan</TH>
                <TH>Type</TH>
                <TH className="text-right">Priority</TH>
                <TH>State</TH>
                <TH className="text-right">Original</TH>
                <TH className="text-right">Recovered</TH>
                <TH className="text-right">Remaining</TH>
                <TH>Settlement</TH>
              </TR>
            </THead>
            <TBody>
              {data.rows.map((r) => (
                <CostRecoveryRow key={r.obligationId} row={r} />
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function CostRecoveryRow({ row }: { row: ObligationLedgerRowDto }) {
  return (
    <TR data-testid="cost-recovery-row">
      <TD>
        {row.loanId ? (
          <Link
            to={`/portfolio-admin/loans/${row.loanId}`}
            className="text-gold-shadow hover:underline"
          >
            {row.loanNumber ?? "—"}
          </Link>
        ) : (
          (row.loanNumber ?? "—")
        )}
      </TD>
      <TD>
        <span className="font-medium">{row.typeDisplay || row.typeCode}</span>
        {row.typeDisplay && (
          <span className="block text-caption text-gray-mid">{row.typeCode}</span>
        )}
      </TD>
      <TD className="text-right tabular-nums">{row.waterfallPriority ?? "—"}</TD>
      <TD>
        <StatusBadge tone={stateTone(row.state)}>{stateLabel(row.state)}</StatusBadge>
      </TD>
      <TD className="text-right tabular-nums">{formatCurrency(row.originalAmount)}</TD>
      <TD className="text-right tabular-nums">{formatCurrency(row.recoveredToDate)}</TD>
      <TD className="text-right tabular-nums">{formatCurrency(row.remaining)}</TD>
      <TD>{channelLabel(row.settlementChannel)}</TD>
    </TR>
  );
}

function SummaryStat({ label, value }: { label: string; value: number }) {
  return (
    <span className="flex flex-col items-end">
      <span className="text-caption uppercase tracking-label text-gray-mid">{label}</span>
      <span className="font-display font-bold text-onyx tabular-nums">
        {formatCurrency(value)}
      </span>
    </span>
  );
}
